import { useCallback, useEffect, useState } from "react";
import { useStateMachine } from "little-state-machine";
import { Button, Form } from "react-bootstrap";
import FocusTrap from "focus-trap-react";
import { useForm } from "react-hook-form";
import { Divider } from "@whitespace/whitespace-ui";
import GroupsFilter from "components/FilterSections/GroupsFilter";
import IDrawer from "interfaces/IDrawer";
import IUserApplicationsFilter from "interfaces/IUserApplicationsFilter";
import { updateUserApplicationsFilter } from "actions/updateUserApplicationsFilter";
import { updateIsUserFilterDrawerOpen } from "actions/updateIsUserFilterDrawerOpen";
import { initialUserApplicationsFilter } from "modules/initialUserApplicationsFilter";
import { ApplicationType } from "interfaces/models/ApplicationType";
import ApplicationTypesFilter from "components/FilterSections/ApplicationTypesFilter";

const UserApplicationsFilterDrawer = ({ isOpen, onClose }: IDrawer) => {
  const { actions } = useStateMachine({
    updateUserApplicationsFilter,
    updateIsUserFilterDrawerOpen
  });

  const { state } = useStateMachine();
  const [hasApplicationTypesFilterChanged, setHasApplicationTypesFilterChanged] = useState(false);
  const [hasGroupsFilterChanged, setHasGroupsFilterChanged] = useState(false);
  const [accordionKey, setAccordionKey] = useState(0);
  const [applyFilterButtonDisabled, setApplyFilterButtonIsDisabled] = useState(true);

  const {
    register, handleSubmit, getValues, setValue
  } = useForm({
    mode: "onSubmit"
  });

  const constructFilterFromForm = useCallback(() => {
    const userApplicationsFilter : IUserApplicationsFilter = initialUserApplicationsFilter();

    const applicationsSelected = getValues("applicationType") as ApplicationType[];

    if (applicationsSelected) {
      if (typeof applicationsSelected === "string") {
        userApplicationsFilter.applicationTypes.push(applicationsSelected);
      } else {
        userApplicationsFilter.applicationTypes = applicationsSelected;
      }
    }

    const groupSelected = getValues("group") as string;

    if (groupSelected) {
      userApplicationsFilter.group = groupSelected;
    }

    return userApplicationsFilter;
  }, [getValues]);

  const handleGroupSelectionChange = (e? : any) => {
    if (e) {
      setValue("group", e.currentTarget.value);
    }
    const filter = constructFilterFromForm();
    setApplyFilterButtonIsDisabled(!(filter.group !== undefined || filter.applicationTypes.length > 0));
  };

  const handleApplicationTypeSelectionChange = () => {
    const filter = constructFilterFromForm();
    setApplyFilterButtonIsDisabled(!(filter.group !== undefined || filter.applicationTypes.length > 0));
  };

  const handleApplyFilterClick = () => {
    const userApplicationsFilter = constructFilterFromForm();
    userApplicationsFilter.searchText = state.userApplicationsFilter!.searchText;
    actions.updateUserApplicationsFilter(userApplicationsFilter);
    onClose();
  };

  const handleResetClick = () => {
    const initialFilter = initialUserApplicationsFilter();
    initialFilter.searchText = state.userApplicationsFilter!.searchText;
    actions.updateUserApplicationsFilter(initialFilter);
    onClose();
  };

  const className = `offcanvas offcanvas-end ${isOpen ? "show" : ""}`;
  const backdropClassName = `backdrop ${!isOpen ? "hidden" : ""}`;
  const tabIndex = isOpen ? 0 : -1;

  const setControlValues = useCallback((userApplicationsFilter : IUserApplicationsFilter) => {
    if (state.isUserApplicationsFilterOpen) {
      setValue("applicationType", userApplicationsFilter.applicationTypes.length > 0 ? userApplicationsFilter.applicationTypes.map(String) : "");
      setValue("group", userApplicationsFilter.group);
      handleGroupSelectionChange();
      handleApplicationTypeSelectionChange();
    }
  }, [setValue, state.isUserApplicationsFilterOpen]);

  useEffect(() => {
    if (state.isUserApplicationsFilterOpen) {
      setAccordionKey(new Date().getTime());
      setHasApplicationTypesFilterChanged(state.userApplicationsFilter !== undefined && state.userApplicationsFilter.applicationTypes.length > 0);
      setHasGroupsFilterChanged(state.userApplicationsFilter !== undefined && state.userApplicationsFilter.group !== undefined);
    }
  }, [state.isUserApplicationsFilterOpen]);

  useEffect(() => {
    setControlValues(state.userApplicationsFilter!);
  }, [setControlValues]);

  // Awaiting Refactor for accessibility.
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <FocusTrap active={process.env.REACT_APP_AMPLIFY_TEST_MODE === "true" ? false : isOpen}>
      <div className="filter-drawer" data-testid="drawer">
        <div className={backdropClassName} onClick={onClose} />

        <Form noValidate onSubmit={handleSubmit(handleApplyFilterClick)}>
          <div className={className} id="offcanvasRight">
            <section>
              <div className="d-flex m-3">
                <h2 className="w-100 m-0" data-testid="filter-header">User Applications Filter</h2>
                <Button type="button" className="btn-close" data-testid="btnUserFilterClose" onClick={onClose} tabIndex={tabIndex} aria-label="Close" />
              </div>
              <Divider />
              <div className="d-flex justify-content-space-between align-content-center">
                <div className="m-3">
                  <Button
                    type="submit"
                    variant="primary"
                    tabIndex={tabIndex}
                    style={{ marginRight: "1em" }}
                    disabled={applyFilterButtonDisabled}
                  >
                    Apply Filter

                  </Button>
                  <Button data-testid="reset-button" variant="secondary" onClick={handleResetClick} tabIndex={tabIndex}>Reset</Button>
                </div>
              </div>
            </section>
            <Divider />
            {isOpen
            && (
              <>
                <ApplicationTypesFilter
                  accordionKey={`group_${accordionKey}`}
                  register={register}
                  applicationTypes={state.applicationTypesFiltered!}
                  hasFilterChanged={hasApplicationTypesFilterChanged}
                  handleOnChange={handleApplicationTypeSelectionChange}
                />
                <GroupsFilter
                  accordionKey={`group_${accordionKey}`}
                  register={register}
                  hasFilterChanged={hasGroupsFilterChanged}
                  groups={state.groupsFilteredUserApplications!}
                  handleOnChange={handleGroupSelectionChange}
                />
              </>
            )}

            <Divider />
          </div>
        </Form>
      </div>
    </FocusTrap>
  );
};
export default UserApplicationsFilterDrawer;
